/* Původ leadu — odkud kontakt přišel, čitelně pro poradce.
 *
 * PROČ TO EXISTUJE (UAT 8/2026, majitel):
 * „Když dám zrcadlo volání a vidím tam ten kontakt, tak tam není žádná
 *  souvislost, odkud ti je. Lead spadne z internetu, z leadovače na
 *  pojišťovnu, komentáře. Není to tam. Nevím, kdo to je."
 *
 * Data přitom V DATABÁZI JSOU — backend/routes/leadgen.py zapisuje
 * `source` jako „Leadovač Pojištění auta", „Trychtýř: penze" nebo „3d-funnel".
 * Chyběl jen překlad do lidské řeči a místo na obrazovce.
 *
 * Modul je čistý (bez Reactu a bez API), aby šel testovat samostatně.
 */
import { CONTACT_SOURCES, CONTACT_SOURCE_ALIASY } from "@/data/crmData";

/* Technické hodnoty `source`, které se ukládají bez lidského popisu.
 * Klíč = přesná hodnota z DB, hodnota = co uvidí poradce. */
const TECHNICKE_ZDROJE = {
  "3d-funnel": { popis: "Web — 3D trychtýř", typ: "web" },
  "leadmagnet": { popis: "Web — stažený materiál", typ: "web" },
  "mesicnik": { popis: "Měsíčník — přihláška z webu", typ: "web" },
  "web_lead": { popis: "Online rezervace", typ: "web" },
  "rezervace": { popis: "Online rezervace", typ: "web" },
  "self": { popis: "Zadáno ručně", typ: "rucne" },
  "import": { popis: "Import ze souboru", typ: "import" },
};

/* Typ (barva chipu) pro položky číselníku; co tu není, je „rucne". */
const TYP_CISELNIKU = {
  recommendation: "doporuceni",
  marketing: "web", reklama: "web", web: "web", soc_sit: "web", web_lead: "web",
  leadovac: "leadovac",
  jine: "jiny",
};

/* Předpony, které leadgen skládá dynamicky (téma se mění). */
const PREFIXY = [
  { test: /^Leadovač\s+(.+)$/i, typ: "leadovac", popis: (m) => `Leadovač — ${m[1]}` },
  { test: /^Trychtýř:\s*(.+)$/i, typ: "trychtyr", popis: (m) => `Trychtýř — ${m[1]}` },
  /* Slovenský poradce dostává zdroj slovensky (backend leadgen.py, 25. 10. 2026). */
  { test: /^Lievik:\s*(.+)$/i, typ: "trychtyr", popis: (m) => `Lievik — ${m[1]}` },
  { test: /^Doporučení(?:\s+od\s+(.+))?$/i, typ: "doporuceni",
    popis: (m) => (m[1] ? `Doporučení od ${m[1]}` : "Doporučení") },
];

/**
 * Přeloží uloženou hodnotu `source` na to, co má poradce vidět.
 *
 * NEZNÁMÝ ZDROJ SE NEZAHAZUJE. Vrátí se tak, jak je v databázi — poradce má
 * radši vidět syrové „kampan-jaro-2026" než nic. Tiché spolknutí by nás vrátilo
 * přesně tam, odkud jdeme.
 *
 * PRÁZDNÝ zdroj ale NENÍ „zadáno ručně". Nevíme, odkud kontakt je, a tvrdit
 * cokoli jiného by bylo vymyšlené — proto `typ: "nezname"`.
 */
export function popisZdroje(source) {
  const raw = String(source ?? "").trim();
  if (!raw) return { popis: "Zdroj neznámý", typ: "nezname", zname: false };

  const znamy = TECHNICKE_ZDROJE[raw.toLowerCase()];
  if (znamy) return { ...znamy, zname: true };

  /* Číselník CONTACT_SOURCES (Projekt 100, Doporučení…) — dřív Zrcadlo ukázalo
     syrové „project100" (CRM-04). */
  const id = CONTACT_SOURCE_ALIASY[raw.toLowerCase()] || raw.toLowerCase();
  const ciselnik = CONTACT_SOURCES.find((z) => z.id === id);
  if (ciselnik) return { popis: ciselnik.label, typ: TYP_CISELNIKU[id] || "rucne", zname: true };

  for (const p of PREFIXY) {
    const m = raw.match(p.test);
    if (m) return { popis: p.popis(m), typ: p.typ, zname: true };
  }
  return { popis: raw, typ: "jiny", zname: true };
}

/* Barva chipu podle typu zdroje. Vrací NÁZEV TOKENU, ne hex — barvy patří
 * do design systému, ne do téhle logiky. */
const BARVY = {
  leadovac: "var(--cat-b)",
  trychtyr: "var(--cat-b)",
  doporuceni: "var(--positive)",
  web: "var(--info)",
  rucne: "var(--muted)",
  import: "var(--muted)",
  jiny: "var(--muted)",
  nezname: "var(--muted)",
};
export const barvaZdroje = (typ) => BARVY[typ] || BARVY.jiny;

/**
 * „Přišel před …" — jak dlouho lead čeká. Vrací null, když datum chybí nebo
 * je nečitelné (radši nic než vymyšlené „dnes").
 *
 * `ted` se předává parametrem, aby šel test napsat bez mockování času.
 * `lang` kvůli SK nástěnce — výchozí čeština, ať se volajícím nic nemění.
 */
export function jakDlouhoCeka(created, ted = new Date(), lang = "cs") {
  if (!created) return null;
  const d = new Date(created);
  if (isNaN(d.getTime())) return null;
  const dny = Math.floor((ted.getTime() - d.getTime()) / 86400000);
  if (dny < 0) return null; // datum v budoucnu = chybná data, netvrdíme nic
  if (dny === 0) return "dnes";
  if (dny === 1) return "včera";   // cs i sk stejně
  return lang === "sk" ? `pred ${dny} dňami` : `před ${dny} dny`;
}

/**
 * Zkrátí poznámku na jeden řádek do fronty volání. Celá poznámka zůstává
 * v klientské složce — tohle je jen upoutávka, ať poradce ví, že tam něco je.
 */
export function zkratPoznamku(text, limit = 90) {
  const s = String(text ?? "").replace(/\s+/g, " ").trim();
  if (!s) return "";
  return s.length <= limit ? s : `${s.slice(0, limit - 1)}…`;
}
